/**
 * Two-pane shell used by every auth screen (login / signup / verify / callback).
 * Left: deep-green brand pane with grid texture + gold accent.
 * Right: white card-style content pane (the form).
 */
export default function AuthLayout({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen w-full flex bg-white" data-testid="auth-layout">
      {/* Left brand pane — hidden on mobile */}
      <div className="hidden lg:flex lg:w-[44%] xl:w-[40%] relative overflow-hidden bg-[#062B1A]">
        {/* subtle grid texture */}
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        {/* accent gradient (within 20% rule) */}
        <div
          className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(212, 175, 55,0.22) 0%, rgba(212, 175, 55,0) 65%)',
          }}
        />
        <div
          className="absolute -bottom-40 -left-20 w-[480px] h-[480px] rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(212, 175, 55,0.10) 0%, rgba(6, 43, 26,0) 70%)',
          }}
        />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center ring-1 ring-[#D4AF37]/40">
              <img src="/assets/mcc-logo-outlined.png" alt="MCC" className="w-9 h-9 object-contain" />
            </div>
            <span
              className="text-xl font-semibold tracking-tight"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              MCC Portal
            </span>
          </div>

          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#D4AF37]">
                Metropolitan Community College
              </span>
            </div>
            <h2
              className="text-4xl xl:text-5xl font-semibold tracking-tight leading-[1.05]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Learn with structure.
              <br />
              <span className="text-[#D4AF37]">Grow with clarity.</span>
            </h2>
            <p className="text-white/70 text-base leading-relaxed max-w-md">
              A focused learning environment for students, instructors, and administrators —
              built for trust, quality, and progress that compounds.
            </p>
          </div>

          <div className="text-xs text-white/40">
            &copy; {new Date().getFullYear()} MCC Portal · Metropolitan Community College
          </div>
        </div>
      </div>

      {/* Right form pane */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-8 py-10">
        <div className="w-full max-w-[440px]">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-[#062B1A] flex items-center justify-center ring-1 ring-[#D4AF37]/30">
              <img src="/assets/mcc-logo-outlined.png" alt="MCC" className="w-7 h-7 object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
            </div>
            <span
              className="text-lg font-semibold text-[#062B1A] tracking-tight"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              MCC Portal
            </span>
          </div>

          <div className="mb-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#D4AF37] mb-3">
              {subtitle}
            </p>
            <h1
              className="text-3xl sm:text-4xl font-semibold text-[#062B1A] tracking-tight"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {title}
            </h1>
          </div>

          {children}

          {footer && (
            <div className="mt-8 text-sm text-[#062B1A]/60 text-center">{footer}</div>
          )}
        </div>
      </div>
    </div>
  );
}
